/**
 * Theme Context for YUL ON TAP
 * 
 * Manages Light/Dark mode with:
 * - Manual preference (persists across sessions permanently)
 * - Time-based prompt (8PM-6AM, session-only, once per day)
 * 
 * Priority: Manual choice always wins over time-based logic
 * 
 * IMPORTANT: When user explicitly chooses dark mode (via toggle or settings),
 * it is saved permanently and will persist across sessions without prompting.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Storage keys
const THEME_PREFERENCE_KEY = 'yul_theme_preference'; // 'light' | 'dark' | null (no manual preference)
const THEME_PROMPT_DATE_KEY = 'yul_theme_prompt_date'; // Date string when prompt was shown/dismissed
const SESSION_DARK_MODE_KEY = 'yul_session_dark_mode'; // 'true' if user accepted time-based dark mode

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Check if current time is between 8PM and 6AM
 */
const isEveningTime = () => {
  const hour = new Date().getHours();
  return hour >= 20 || hour < 6;
};

/**
 * Get today's date as a string (for tracking prompt shown date)
 */
const getTodayString = () => {
  return new Date().toISOString().split('T')[0];
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage - manual preference takes priority
  const [theme, setThemeState] = useState(() => {
    const manualPref = localStorage.getItem(THEME_PREFERENCE_KEY);
    const sessionDark = sessionStorage.getItem(SESSION_DARK_MODE_KEY);
    
    // Manual preference always wins
    if (manualPref === 'dark' || manualPref === 'light') {
      return manualPref;
    } else if (sessionDark === 'true') {
      return 'dark';
    }
    return 'light';
  });
  
  const [showTimePrompt, setShowTimePrompt] = useState(false);
  
  const [isManualPreference, setIsManualPreference] = useState(() => {
    const manualPref = localStorage.getItem(THEME_PREFERENCE_KEY);
    return manualPref === 'dark' || manualPref === 'light';
  });

  // Apply theme class to document
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  /**
   * Check if we should show the time-based prompt
   * Called after login
   * 
   * NOTE: Only shows prompt if user has NOT set a manual preference.
   * If user has chosen dark mode manually, they will NEVER see this prompt.
   */
  const checkTimeBasedPrompt = useCallback(() => {
    // Don't show if user has manual preference (they've already made their choice)
    const manualPref = localStorage.getItem(THEME_PREFERENCE_KEY);
    if (manualPref === 'dark' || manualPref === 'light') {
      return false;
    }

    // Don't show if already shown today
    const lastPromptDate = localStorage.getItem(THEME_PROMPT_DATE_KEY);
    if (lastPromptDate === getTodayString()) {
      return false;
    }

    // Don't show if not evening time
    if (!isEveningTime()) {
      return false;
    }

    // Don't show if already in dark mode this session
    const sessionDark = sessionStorage.getItem(SESSION_DARK_MODE_KEY);
    if (sessionDark === 'true') {
      return false;
    }

    // Show the prompt
    setShowTimePrompt(true);
    return true;
  }, []);

  /**
   * Handle user accepting the time-based prompt
   * Dark mode for this session only
   */
  const acceptTimeBasedDarkMode = useCallback(() => {
    setThemeState('dark');
    sessionStorage.setItem(SESSION_DARK_MODE_KEY, 'true');
    localStorage.setItem(THEME_PROMPT_DATE_KEY, getTodayString());
    setShowTimePrompt(false);
  }, []);

  /**
   * Handle user declining the time-based prompt
   */
  const declineTimeBasedDarkMode = useCallback(() => {
    localStorage.setItem(THEME_PROMPT_DATE_KEY, getTodayString());
    setShowTimePrompt(false);
  }, []);

  /**
   * Manually set theme preference (persists PERMANENTLY across sessions)
   * This is what users expect when they toggle dark mode - it should STAY dark
   */
  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
    setIsManualPreference(true);
    // Save permanently to localStorage - this is the key persistence mechanism
    localStorage.setItem(THEME_PREFERENCE_KEY, newTheme);
    // Clear session-only dark mode since user made a manual choice
    sessionStorage.removeItem(SESSION_DARK_MODE_KEY);
    // Also clear the prompt date so it never shows again for this user
    // (they've made their choice)
  }, []);

  /**
   * Toggle between light and dark mode (manual preference - PERMANENT)
   * When user clicks the toggle, their choice persists forever until changed
   */
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [theme, setTheme]);

  /**
   * Clear manual preference (reset to default behavior)
   */
  const clearPreference = useCallback(() => {
    localStorage.removeItem(THEME_PREFERENCE_KEY);
    sessionStorage.removeItem(SESSION_DARK_MODE_KEY);
    setThemeState('light');
    setIsManualPreference(false);
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        isDark: theme === 'dark',
        isLight: theme === 'light',
        isManualPreference,
        showTimePrompt,
        setTheme,
        toggleTheme,
        clearPreference,
        checkTimeBasedPrompt,
        acceptTimeBasedDarkMode,
        declineTimeBasedDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
